import React, { useState } from 'react';
import { Trash2, Edit, User, SearchIcon, X, FolderOpen, UserCircle, RefreshCw } from 'lucide-react';
import { Button, Input, Spinner, Tooltip } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { User as UserType } from '@/services/apis/users';
import { BaseTable, TableColumn } from '../../base';
import { BaseTableCell } from '../../base/BaseTable';
import ConfirmationModal from '../../base/ConfirmationModal';
import DepartmentSelector from '../../base/selectors/DepartmentSelector';
import RoleSelector from '../../base/selectors/RoleSelector';
import DepartmentsModal from './DepartmentsModal';
import { useDepartments } from '@/hooks/queries/useDepartments';

interface UsersTabProps {
  users: UserType[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  error: string | null;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedDepartmentId?: string;
  onDepartmentChange: (value: string | undefined) => void;
  selectedRole?: string;
  onRoleChange: (value: string | undefined) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onEdit: (user: UserType) => void;
  onDelete: (user: UserType) => void;
  deleteModalOpen: boolean;
  selectedUser: UserType | null;
  onDeleteConfirm: () => void;
  onDeleteModalClose: () => void;
  isDeleting: boolean;
  loadMoreRef: React.RefObject<HTMLElement> | ((node: HTMLElement | null) => void);
  hasNextPage: boolean;
  onCreate: () => void;
  onSyncFromSlack: () => void;
  isSyncing: boolean;
}

const UsersTab: React.FC<UsersTabProps> = ({
  users,
  isLoading,
  isFetchingNextPage,
  error,
  searchQuery,
  onSearchChange,
  selectedDepartmentId,
  onDepartmentChange,
  selectedRole,
  onRoleChange,
  hasActiveFilters,
  onClearFilters,
  onEdit,
  onDelete,
  deleteModalOpen,
  selectedUser: _selectedUser,
  onDeleteConfirm,
  onDeleteModalClose,
  isDeleting,
  loadMoreRef,
  hasNextPage,
  onCreate,
  onSyncFromSlack,
  isSyncing,
}) => {
  const { t } = useTranslation('admin');
  const [isDepartmentsModalOpen, setIsDepartmentsModalOpen] = useState(false);
  const { data: departments = [], isLoading: isLoadingDepartments } = useDepartments();

  const userColumns: TableColumn[] = [
    { name: t('users.table.columns.name', { defaultValue: 'Name' }), uid: 'name', sortable: true },
    { name: t('users.table.columns.email', { defaultValue: 'Email' }), uid: 'email', sortable: true },
    { name: t('users.table.columns.role', { defaultValue: 'Role' }), uid: 'role', sortable: true },
    { name: t('users.table.columns.department', { defaultValue: 'Department' }), uid: 'department', sortable: true },
    { name: t('users.table.columns.position', { defaultValue: 'Position' }), uid: 'position', sortable: true },
    { name: t('users.table.columns.actions', { defaultValue: 'Actions' }), uid: 'actions', sortable: false },
  ];

  const renderCell = (user: UserType, columnKey: string) => {
    switch (columnKey) {
      case 'name':
        return (
          <BaseTableCell
            text={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
            icon={<User className="w-5 h-5" />}
          />
        );

      case 'email':
        return <BaseTableCell text={user.email} />;

      case 'role':
        return (
          <BaseTableCell
            text={
              user.role === 'admin'
                ? t('users.roles.admin', { defaultValue: 'Admin' })
                : user.role === 'creator'
                ? t('users.roles.creator', { defaultValue: 'Creator' })
                : t('users.roles.learner', { defaultValue: 'Learner' })
            }
          />
        );

      case 'department':
        return <BaseTableCell text={user.department?.name || t('common.none', { defaultValue: 'None' })} />;

      case 'position':
        return <BaseTableCell text={user.position || t('common.notSpecified', { defaultValue: 'Not specified' })} />;

      case 'actions':
        return (
          <div className="flex items-center space-x-2">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => onEdit(user)}
              aria-label={t('users.actions.edit', { defaultValue: 'Edit' })}
              title={t('users.actions.edit', { defaultValue: 'Edit' })}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => onDelete(user)}
              aria-label={t('users.actions.delete', { defaultValue: 'Delete' })}
              title={t('users.actions.delete', { defaultValue: 'Delete' })}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('users.title', { defaultValue: 'Users' })}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('users.subtitle', { defaultValue: 'Manage users and their permissions' })}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="flat"
            startContent={<FolderOpen className="w-4 h-4" />}
            onPress={() => setIsDepartmentsModalOpen(true)}
          >
            {t('users.manageDepartments', { defaultValue: 'Manage Departments' })}
          </Button>
          <Tooltip
            content={t('users.syncFromSlackTooltip', {
              defaultValue: 'Import new users and update profile pictures from your Slack workspace',
            })}
          >
            <Button
              variant="flat"
              startContent={<RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />}
              onPress={onSyncFromSlack}
              isDisabled={isSyncing}
            >
              {t('users.syncFromSlack', { defaultValue: 'Sync from Slack' })}
            </Button>
          </Tooltip>
          <Button color="primary" onPress={onCreate} isDisabled={isSyncing}>
            {t('users.create', { defaultValue: 'Create User' })}
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-between gap-3 items-end flex-wrap">
        <Input
          isClearable
          className="w-full sm:max-w-[44%]"
          placeholder={t('users.search.placeholder', { defaultValue: 'Search users...' })}
          startContent={<SearchIcon />}
          value={searchQuery}
          onClear={() => onSearchChange('')}
          onValueChange={onSearchChange}
        />
        <div className="flex gap-3 flex-wrap">
          <div className="w-[180px]">
            <DepartmentSelector
              departments={departments}
              isLoading={isLoadingDepartments}
              value={selectedDepartmentId}
              onChange={(value) => onDepartmentChange(value || undefined)}
              placeholder={t('users.filters.department', { defaultValue: 'All Departments' })}
              variant="flat"
              label={t('users.form.department.label', { defaultValue: 'Department' })}
              showEmptyOption
            />
          </div>
          <div className="w-[150px]">
            <RoleSelector
              value={selectedRole}
              onChange={(value) => onRoleChange(value || undefined)}
              placeholder={t('users.filters.role', { defaultValue: 'All Roles' })}
              variant="flat"
              label=""
              showEmptyOption
            />
          </div>
          {hasActiveFilters && (
            <Button variant="flat" onPress={onClearFilters} startContent={<X className="w-4 h-4" />}>
              {t('common.clearFilters', { defaultValue: 'Clear Filters' })}
            </Button>
          )}
        </div>
      </div>

      {/* Users Table */}
      <BaseTable
        data={users}
        columns={userColumns}
        isLoading={isLoading && users.length === 0}
        loadingMessage={t('common.loading', { defaultValue: 'Loading...' })}
        error={error}
        emptyMessage={t('users.empty', { defaultValue: 'No user record' })}
        emptyIcon={<UserCircle className="w-12 h-12 text-gray-400" />}
        renderCell={renderCell}
        stickyHeader={true}
        hoverable={true}
        striped={true}
        idField="id"
      />

      {/* Infinite Scroll Trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef as React.RefObject<HTMLDivElement>} className="flex justify-center py-4">
          {isFetchingNextPage && <Spinner size="lg" />}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={onDeleteModalClose}
        onConfirm={onDeleteConfirm}
        title={t('users.delete.title', { defaultValue: 'Delete User' })}
        message={t('users.delete.message', {
          defaultValue: 'Are you sure you want to delete this user? This action cannot be undone.',
        })}
        confirmLabel={t('common.delete', { defaultValue: 'Delete' })}
        cancelLabel={t('common.cancel', { defaultValue: 'Cancel' })}
        confirmColor="danger"
        isLoading={isDeleting}
      />

      {/* Departments Management Modal */}
      <DepartmentsModal isOpen={isDepartmentsModalOpen} onClose={() => setIsDepartmentsModalOpen(false)} />
    </div>
  );
};

export default UsersTab;
